import { isWeightTotalComplete, sumWeights } from "@repo/shared";
import type { LocalizedString, PortfolioCollectionListItem } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Switch } from "@/components/ui/switch";
import {
  rowControl,
  Table,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as pcApi from "@/lib/api/portfolio-collections";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import {
  useDeletePortfolio,
  usePlatforms,
  useTogglePortfolioActive,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";

import { PortfolioDialog } from "../../portfolio-dialog";
import { PlatformBadges, WeightTotalCell } from "../portfolio-weights";
import { T } from "./portfolios-table.i18n";

type PortfoliosTableProps = {
  collection: PortfolioCollectionListItem;
};

export function PortfoliosTable({ collection }: PortfoliosTableProps) {
  const localize = useLocalize();
  const t = useT(T);

  // Platform labels for the visibility badges (rows store platform row ids).
  const { platforms } = usePlatforms(collection.id);
  const deletePortfolio = useDeletePortfolio();
  const togglePortfolioActive = useTogglePortfolioActive();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState<{
    id: string;
    nameI18n: LocalizedString;
    descriptionI18n: LocalizedString | null;
    riskScore: number;
    platformIds: string[];
  } | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  // QL-168 — a portfolio the server would refuse to delete (the last active
  // cover of a risk score) shows why on the Delete click, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (portfolioId) => pcApi.checkDeletePortfolio(collection.id, portfolioId),
    onAllowed: (portfolioId) => {
      setPortfolioToDelete(portfolioId);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDelete, "destructive"),
  });

  const handleToggleActive = async (id: string) => {
    togglePortfolioActive.mutate(
      {
        collectionId: collection.id,
        portfolioId: id,
      },
      {
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToUpdate, "destructive");
        },
      },
    );
  };

  const handleDelete = async () => {
    if (!portfolioToDelete) return;

    deletePortfolio.mutate(
      {
        collectionId: collection.id,
        portfolioId: portfolioToDelete,
      },
      {
        onSuccess: () => {
          toast.success(t.portfolioDeleted);
          setPortfolioToDelete(null);
        },
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToDelete, "destructive");
          setPortfolioToDelete(null);
        },
      },
    );
  };

  const getRiskScoreRange = () => {
    if (!collection.riskModel) return { min: 1, max: 20 };
    const scores = collection.riskModel.matrixScores.map((ms) => ms.score);
    if (scores.length === 0) return { min: 1, max: 20 };
    return {
      min: Math.min(...scores),
      max: Math.max(...scores),
    };
  };

  const riskScoreRange = getRiskScoreRange();

  const getRiskScoreDescription = () => {
    if (!collection.riskModel) return t.noRiskModelDesc;
    return t.riskScoreDesc({
      min: String(riskScoreRange.min),
      max: String(riskScoreRange.max),
      name: localize(collection.riskModel.nameI18n),
    });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">{getRiskScoreDescription()}</p>
          <Button onClick={() => setDialogOpen(true)}>{t.addPortfolio}</Button>
        </div>

        {collection.portfolios.length === 0 ? (
          <EmptyState>{t.noPortfolios}</EmptyState>
        ) : (
          <Table variant="navigation">
            <TableHeader>
              <TableRow>
                <TableHead>{t.riskScore}</TableHead>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.products}</TableHead>
                <TableHead>{t.platforms}</TableHead>
                <TableHead>{t.weight}</TableHead>
                <TableHead>{t.active}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {collection.portfolios.map((portfolio) => {
                const weights = portfolio.products.map((product) => Number(product.weight));

                return (
                  <TableRow key={portfolio.id}>
                    <TableCell>
                      <div className="text-lg font-bold">{portfolio.riskScore}</div>
                    </TableCell>
                    <TableCellLink
                      to={`/settings/portfolios/${collection.id}/portfolio/${portfolio.id}`}
                    >
                      <div>
                        <div className="font-medium">{localize(portfolio.nameI18n)}</div>
                        {localize(portfolio.descriptionI18n) && (
                          <div className="text-sm text-muted-foreground">
                            {localize(portfolio.descriptionI18n)}
                          </div>
                        )}
                      </div>
                    </TableCellLink>
                    <TableCell>
                      <span className="text-muted-foreground">
                        {portfolio.products.length}{" "}
                        {portfolio.products.length === 1 ? t.product : t.productsPlural}
                      </span>
                    </TableCell>
                    <TableCell>
                      <PlatformBadges
                        platformIds={portfolio.platformIds ?? []}
                        platforms={platforms}
                      />
                    </TableCell>
                    <TableCell>
                      <WeightTotalCell
                        total={sumWeights(weights)}
                        isComplete={isWeightTotalComplete(weights)}
                      />
                    </TableCell>
                    <TableCell className={rowControl}>
                      <Switch
                        aria-label={`${t.active}: ${localize(portfolio.nameI18n)}`}
                        checked={portfolio.isActive}
                        onCheckedChange={() => handleToggleActive(portfolio.id)}
                      />
                    </TableCell>
                    <TableCell className={cn("text-right", rowControl)}>
                      <div className="flex items-center justify-end">
                        <Button
                          size="icon"
                          tooltip={t.edit}
                          variant="ghost"
                          onClick={() => {
                            setEditingPortfolio({
                              id: portfolio.id,
                              nameI18n: portfolio.nameI18n,
                              descriptionI18n: portfolio.descriptionI18n,
                              riskScore: portfolio.riskScore,
                              platformIds: portfolio.platformIds ?? [],
                            });
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          tooltip={t.delete}
                          variant="ghost"
                          onClick={() => requestDelete(portfolio.id)}
                          loading={checkingId === portfolio.id}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <PortfolioDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingPortfolio(null);
          }
        }}
        portfolioCollectionId={collection.id}
        existingRiskScores={collection.portfolios.map((p) => p.riskScore)}
        portfolio={editingPortfolio}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deletePortfolioTitle}
        description={t.deletePortfolioDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
